import type { BoardDef, ReplaySummary } from '@archon3d/core';
import { useEffect, useState } from 'react';
import type { ServerStatus } from '../../net/serverClient';
import type { RoomController } from '../../net/useRoom';
import { clockLabel } from './OnlinePanels';
import { serverLabel } from './serverLabel';

interface Props {
  server: ServerStatus;
  boards: BoardDef[];
  room: RoomController;
  onBack: () => void;
}

/** The finished games the server still remembers, each a click away from playing back. */
export function ReplaysPanel({ server, boards, room, onBack }: Props) {
  const [replays, setReplays] = useState<ReplaySummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fetching, setFetching] = useState<string | null>(null);
  const online = server.state === 'online';
  const { listReplays, fetchReplay, watchReplay } = room;

  useEffect(() => {
    if (!online) return;
    let cancelled = false;
    listReplays()
      .then((list) => {
        if (!cancelled) setReplays(list);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [online, listReplays]);

  const watch = (id: string) => {
    setFetching(id);
    setError(null);
    fetchReplay(id)
      .then((replay) => watchReplay(replay))
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
        setFetching(null);
      });
  };

  return (
    <>
      <h2 className="menu-heading">Replays</h2>
      <p className="menu-text">
        The server keeps its last ten finished games. A replay steps through the moves at your pace
        and fights every duel again exactly as it went, seen from the stands.
      </p>
      {online && replays === null && !error && <p className="hint">Asking the server…</p>}
      {replays && replays.length === 0 && (
        <p className="hint">No finished games yet; the shelf is empty until a game ends.</p>
      )}
      {replays && replays.length > 0 && (
        <ul className="replay-list" data-testid="replay-list">
          {replays.map((summary) => (
            <ReplayRow
              key={summary.id}
              summary={summary}
              board={
                summary.setup.definition?.board ??
                boards.find((b) => b.id === summary.setup.boardId)
              }
              busy={fetching === summary.id}
              onWatch={() => watch(summary.id)}
            />
          ))}
        </ul>
      )}
      <p className="hint">{serverLabel(server)}</p>
      {error && <p className="hint warn">{error}</p>}
      <div className="menu-actions">
        <button onClick={onBack}>Back</button>
      </div>
    </>
  );
}

function ReplayRow({
  summary,
  board,
  busy,
  onWatch,
}: {
  summary: ReplaySummary;
  board: BoardDef | undefined;
  busy: boolean;
  onWatch: () => void;
}) {
  const players = summary.seats
    .map((seat, i) => (seat === 'human' ? summary.names[i] || `Player ${i + 1}` : 'AI'))
    .join(' vs ');
  const winner =
    summary.winner === null
      ? 'unfinished'
      : summary.winner === 'draw'
        ? 'a draw'
        : `${board?.factions.find((f) => f.id === summary.winner)?.name ?? summary.winner} wins`;
  return (
    <li>
      <div>
        <div className="replay-title">
          {players} · {winner}
        </div>
        <div className="replay-meta">
          {board?.name ?? summary.setup.boardId} · {summary.moves} moves · {summary.duels}{' '}
          {summary.duels === 1 ? 'duel' : 'duels'} · {clockLabel(summary.setup.clockSeconds)} ·{' '}
          {timeAgo(summary.finishedAt)}
        </div>
      </div>
      <button className="primary" disabled={busy} onClick={onWatch} data-testid="replay-watch">
        {busy ? 'Loading…' : 'Watch'}
      </button>
    </li>
  );
}

/** "just now", "5 min ago", "2 h ago": the list is short-lived, so no dates. */
export function timeAgo(finishedAt: number, now = Date.now()): string {
  const minutes = Math.max(0, Math.round((now - finishedAt) / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}
